import { ComponentRef, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';
import { APP_ROUTER_TIME_CACHE } from '@constants/app.constant';
import { get } from 'lodash';

type StoreRoute = {
    time: number;
    handle: DetachedRouteHandle;
    storeWhenNavigate: string[];
};

@Injectable()
export default class CustomRouterReuseStrategy implements RouteReuseStrategy {
    private readonly storedRoutes = new Map<string, StoreRoute>();

    /**
     * Check store when navigate
     *
     * @private
     * @param {ActivatedRouteSnapshot} future
     * @param {ActivatedRouteSnapshot} curr
     * @return {*}  {void}
     * @memberof CustomRouterReuseStrategy
     */
    private _checkStoreWhenNavigate(
        future: ActivatedRouteSnapshot,
        curr: ActivatedRouteSnapshot
    ): void {
        const isLast = future.routeConfig && 'loadComponent' in future.routeConfig;
        if (!isLast) {
            return;
        }

        const currentPath = this._path(curr);
        const futurePath = this._path(future);
        for (const key of this.storedRoutes.keys()) {
            const { storeWhenNavigate, handle } = this.storedRoutes.get(key) || {};
            const isSameCurrentPath =
                storeWhenNavigate?.length && storeWhenNavigate.includes(currentPath);
            const isSameFuturePath = key === futurePath;
            if (isSameCurrentPath && !isSameFuturePath) {
                this._destroyComponentRef(key, handle);
            }
        }
    }

    /**
     * Destroy component ref
     *
     * @private
     * @param {string} path
     * @param {DetachedRouteHandle} [handle]
     * @memberof CustomRouterReuseStrategy
     */
    private _destroyComponentRef(path: string, handle?: DetachedRouteHandle): void {
        const componentRef = get(handle, 'componentRef');
        if (componentRef && 'destroy' in componentRef) {
            (componentRef as ComponentRef<any>).destroy();
        }
        this.storedRoutes.delete(path);
    }

    /**
     * Get path from route
     *
     * @private
     * @param {ActivatedRouteSnapshot} route
     * @return {*}  {string}
     * @memberof CustomRouterReuseStrategy
     */
    private _path(route: ActivatedRouteSnapshot): string {
        return route.routeConfig?.path ?? route.url.join('/');
    }

    /**
     * Check if route should be detached
     *
     * @param {ActivatedRouteSnapshot} route
     * @return {*}  {boolean}
     * @memberof CustomRouterReuseStrategy
     */
    shouldDetach(route: ActivatedRouteSnapshot): boolean {
        return !!route.routeConfig?.data?.['store'];
    }

    /**
     * Store detached route
     *
     * @param {ActivatedRouteSnapshot} route
     * @param {(DetachedRouteHandle | null)} handle
     * @memberof CustomRouterReuseStrategy
     */
    store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
        if (handle) {
            const time = Date.now();
            this.storedRoutes.set(this._path(route), {
                time,
                handle,
                storeWhenNavigate: route.data['storeWhenNavigate'] || [],
            });
        }
    }

    /**
     * Check if route should be attached
     *
     * @param {ActivatedRouteSnapshot} route
     * @return {*}  {boolean}
     * @memberof CustomRouterReuseStrategy
     */
    shouldAttach(route: ActivatedRouteSnapshot): boolean {
        const path = this._path(route);
        const storedRoute = this.storedRoutes.get(path);
        if (!storedRoute) return false;

        const time = Date.now();

        // Check if force reload
        const forceReload = get(storedRoute.handle, 'componentRef.instance.forceReload', false);
        if (time - storedRoute.time > APP_ROUTER_TIME_CACHE || forceReload) {
            this._destroyComponentRef(path, storedRoute.handle);
            return false;
        }
        return true;
    }

    /**
     * Retrieve stored route
     *
     * @param {ActivatedRouteSnapshot} route
     * @return {*}  {(DetachedRouteHandle | null)}
     * @memberof CustomRouterReuseStrategy
     */
    retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        return this.storedRoutes.get(this._path(route))?.handle || null;
    }

    /**
     * Check if route should be reused
     *
     * @param {ActivatedRouteSnapshot} future
     * @param {ActivatedRouteSnapshot} curr
     * @return {*}  {boolean}
     * @memberof CustomRouterReuseStrategy
     */
    shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
        this._checkStoreWhenNavigate(future, curr);
        return future.routeConfig === curr.routeConfig;
    }
}
